import { FlagIcon } from '../Icons/FlagIcon';
import React, { useMemo, useState } from 'react';
import { FlagContainer, FlagIconContainer } from './styles';
import EnglandIcon from '../../assets/images/iconsMvpApp/UK of GB and Northern Ireland.svg';
import DenmarkIcon from '../../assets/images/iconsMvpApp/Denmark.svg';
import NorwayIcon from '../../assets/images/iconsMvpApp/Norway.svg';
import { createLanguageMenuLinks } from '../../routes';
import moment from 'moment/moment';
import { getGlobal } from '../../utils/getGlobal';

const defaultConfig = {
  getLanguageCode: () => 'en',
  getSupportedLanguageCodes: () => ['en', 'da', 'no'],
  setLanguage: () => {},
};

const LANGUAGE_METADATA: Record<string, { label: string; icon: string }> = {
  en: { label: 'English', icon: EnglandIcon },
  da: { label: 'Danish', icon: DenmarkIcon },
  no: { label: 'Norsk', icon: NorwayIcon },
};

const getConfigModule = () => {
  try {
    const stateMod = require('../../state');
    return stateMod?.Config ? stateMod.Config : defaultConfig;
  } catch {
    return defaultConfig;
  }
};

interface CountryFlagWithLanguage {
  location?: { pathname: string };
  history?: { push: (path: string) => void };
}

export const CountryFlagWithLanguage = ({
  location,
  history,
}: CountryFlagWithLanguage) => {
  const ConfigModule = useMemo(() => getConfigModule(), []);
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    ConfigModule.getLanguageCode()
  );
  const supportedLanguageCodes = ConfigModule.getSupportedLanguageCodes();

  const pathname = location?.pathname || globalThis?.location.pathname || '/';

  const languageMenuLinks = useMemo(
    () =>
      createLanguageMenuLinks(
        pathname,
        currentLanguage,
        supportedLanguageCodes
      ),
    [pathname, currentLanguage, supportedLanguageCodes]
  );

  const mapMomentLocale = (appCode: string) => {
    const map: Record<string, string> = {
      en: 'en-gb',
      no: 'nb',
      da: 'da',
    };
    return map[appCode] || appCode;
  };

  const getTargetPath = (
    link:
      | { path?: string; href?: string; link?: string; to?: string }
      | undefined,
    currentPath: string,
    srcLang: string,
    targetLang: string
  ) => {
    if (link) {
      const candidate = link.path || link.href || link.link || link.to;
      if (candidate) return candidate;
    }
    const src = srcLang || '';
    const escaped = src.replaceAll(/[-/\\^$*+?.()|[\]{}]/g, String.raw`\$&`);
    return (
      currentPath.replace(
        new RegExp(`^/${escaped}(?=/|$)`),
        `/${targetLang}`
      ) || '/'
    );
  };

  const handleLanguageToggle = async (targetCode: string) => {
    await import(`../../locale/${targetCode}.json`);

    ConfigModule.setLanguage(targetCode);

    const momentLocale = mapMomentLocale(targetCode);
    await import(`moment/locale/${momentLocale}`).catch(() => {});
    moment.locale(momentLocale);

    setCurrentLanguage(targetCode);

    const targetPath = getTargetPath(
      languageMenuLinks.find(l => l.languageCode === targetCode),
      pathname,
      currentLanguage,
      targetCode
    );

    const g = getGlobal();
    if (history && typeof history.push === 'function') {
      history.push(targetPath);
    } else if (g.window !== 'undefined') {
      g.location.assign(targetPath);
    }
  };

  const getNextLanguage = (current: string) => {
    const list = supportedLanguageCodes?.length
      ? supportedLanguageCodes
      : ['en', 'da', 'no'];
    const idx = list.indexOf(current);
    const nextIndex = idx === -1 ? 0 : (idx + 1) % list.length;
    return list[nextIndex];
  };

  const onLanguageButtonClick = async () => {
    const target = getNextLanguage(currentLanguage);
    await handleLanguageToggle(target);
  };

  const nextLanguage = getNextLanguage(currentLanguage);
  const nextMeta = LANGUAGE_METADATA[nextLanguage] || {
    label: nextLanguage.toUpperCase(),
    icon: EnglandIcon,
  };

  return (
    <FlagContainer
      aria-label={nextMeta.label}
      tabIndex={0}
      onClick={onLanguageButtonClick}
    >
      <FlagIconContainer>
        <FlagIcon icon={nextMeta.icon} />
        {nextMeta.label}
      </FlagIconContainer>
    </FlagContainer>
  );
};
